
import { Injectable } from '@angular/core';
import { SampleReconciliation } from '@omni/classes/sample/sample-reconciliation.class';
import { fetchQueries } from '@omni/config/dynamics-fetchQueries';
import { DynamicsClientService } from '@omni/data-services/dynamics-client/dynamics-client.service';
import { DiskService } from '../disk/disk.service';
import { DB_KEY_PREFIXES, DB_SYNC_STATE_KEYS } from '@omni/config/pouch-db.config';
import { BehaviorSubject } from 'rxjs';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { Endpoints } from 'src/config/endpoints.config';
import { AuthenticationService } from '../authentication.service';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '@omni/classes/authentication/user.class';
import { HttpClient } from '@angular/common/http';
import { AlertService } from '../alert/alert.service';
import { TranslateService } from '@ngx-translate/core';


@Injectable({
  providedIn: 'root'
})

export class SampleReconciliationService {
  sampleReconciliationRequests: SampleReconciliation[] = [];
  selectedReconciliationRequestId: string = '';
  selectedReconciliationRequest: BehaviorSubject<SampleReconciliation> = new BehaviorSubject<SampleReconciliation>(null);
  selectedReconciliationLineItems: any[] = [];
  reconciliationRequestsUpdated: BehaviorSubject<SampleReconciliation[]> = new BehaviorSubject<SampleReconciliation[]>([]);
  searchEventSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');

  /** True when user is in create mode and has entered data (purpose, physical qty, or manually added items). */
  hasUnsavedCreateModeChanges: boolean = false;

  constructor(
    private dynamics: DynamicsClientService,
    private disk : DiskService,
    private authservice: AuthenticationService,
    private http: HttpClient,
    private alertService: AlertService,
    private translate: TranslateService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
  ){}

  async fetchReconciliationRequests(forceFullSync: boolean, loadFromDBOnly: boolean): Promise<void> {
    if (!this.authservice.hasFeatureAction(FeatureActionsMap.SAMPLE_RECONCILIATIONS)) return;
    if (loadFromDBOnly) return;
    await this.syncReconciliationRequestsFromServer(forceFullSync);
  }

  /**
   * Syncs reconciliation requests from Dynamics server
   */
  private async syncReconciliationRequestsFromServer(forceFullSync: boolean): Promise<void> {
    const syncState = await this.disk.getSyncState(DB_SYNC_STATE_KEYS.SYNC_SAMPLE_RECONCILIATION_REQUESTS);
    const isInitialSync = forceFullSync || this.sampleReconciliationRequests.length === 0 || !syncState || !syncState.lastUpdatedTime;
    
    const fetchXml = this.buildFetchXmlWithDeltaSync(isInitialSync, syncState);

    try {
      const serverRequests = await this.dynamics.executeFetchQuery('indskr_samplerequests', fetchXml);
      
      if (!Array.isArray(serverRequests) || serverRequests.length === 0) {
        await this.handleEmptyServerResponse(isInitialSync, syncState);
        return;
      }

      await this.updateReconciliationRequestsInMemory(serverRequests, syncState, isInitialSync);
    } catch (error) {
      console.error('Error fetching reconciliation requests from server:', error);
      await this.handleFetchError();
    }
  }

  /**
   * Builds fetch XML with appropriate delta sync filter
   */
  private buildFetchXmlWithDeltaSync(isInitialSync: boolean, syncState: any): string {
    let fetchXml = fetchQueries.sampleReonciliation.fetchReconciliationRequestsByUserId;
    
    if (isInitialSync) {
      return fetchXml.replace('{deltaSyncFilter}', '');
    }

    const now = new Date();
    const lastSyncTime = new Date(syncState.lastUpdatedTime);
    let hourDifference = this.dateTimeFormatsService.getDifferenceInHours(now, lastSyncTime);
    hourDifference += 1; // Add one to account for fractional hour differences
    
    const deltaSyncFilter = `<condition attribute="modifiedon" operator="last-x-hours" value="${hourDifference}" />`;
    return fetchXml.replace('{deltaSyncFilter}', deltaSyncFilter);
  }

  /**
   * Updates reconciliation requests in memory and sync state
   */
  private async updateReconciliationRequestsInMemory(serverRequests: any[], syncState: any, isInitialSync: boolean): Promise<void> {
    try {
      if (isInitialSync) {
        // Initial sync: replace all requests
        this.sampleReconciliationRequests = serverRequests.map(request => new SampleReconciliation(request));
      } else {
        // Delta sync: merge/update existing requests
        const newRequests = serverRequests.map(request => new SampleReconciliation(request));
        const existingRequestsMap = new Map(
          this.sampleReconciliationRequests.map(req => [req.id, req])
        );
        
        // Update or add new requests
        for (const newRequest of newRequests) {
          existingRequestsMap.set(newRequest.id, newRequest);
        }
        
        this.sampleReconciliationRequests = Array.from(existingRequestsMap.values());
      }
      
      this.reconciliationRequestsUpdated.next(this.sampleReconciliationRequests);
      
      // Initialize syncState if it's null or undefined (first sync - no _id/_rev yet)
      const syncStateKey = DB_SYNC_STATE_KEYS.SYNC_SAMPLE_RECONCILIATION_REQUESTS;
      const updatedSyncState = syncState || { _id: syncStateKey };
      updatedSyncState.lastUpdatedTime = new Date().getTime();
      const useUpsert = !syncState || !syncState._id || !syncState._rev;
      await this.disk.updateSyncState(updatedSyncState, useUpsert);
    } catch (error) {
      console.error('Error updating reconciliation requests:', error);
      throw error;
    }
  }

  /**
   * Handles empty server response during initial sync
   */
  private async handleEmptyServerResponse(isInitialSync: boolean, syncState: any): Promise<void> {
    if (!isInitialSync) {
      return;
    }

    try {
      this.sampleReconciliationRequests = [];
      this.reconciliationRequestsUpdated.next(this.sampleReconciliationRequests);
    } catch (error) {
      console.error('Error clearing reconciliation requests:', error);
    }
  }

  /**
   * Handles fetch errors by clearing in-memory data
   */
  private async handleFetchError(): Promise<void> {
    try {
      this.sampleReconciliationRequests = [];
      this.reconciliationRequestsUpdated.next(this.sampleReconciliationRequests);
    } catch (error) {
      console.error('Error handling fetch error:', error);
    }
  }

  async fetchReconciliationLineItemsByRequestId(requestId: string) {
    try {
      const fetchXml = fetchQueries.sampleReonciliation.fetchReconciliationLineItemsByRequestId;
      const fetchXmlWithRequestId = fetchXml.replace('{requestId}', requestId);
      const reconciliationLineItems = await this.dynamics.executeFetchQuery('indskr_samplereconciliations', fetchXmlWithRequestId);
      return reconciliationLineItems;
    } catch (error) {
      console.log(error);
      return [];
    }
  }

  /**
   * Item 7 (PDMA): returns the set of lot ids (lowercased) that the current user has already
   * reconciled with a physical on-hand qty of 0, based on their latest submission per lot. Used to
   * exclude those lots from the default create-mode list while keeping them searchable to re-add.
   * Online-only (the feature has no offline cache); returns an empty set on error/offline.
   */
  async fetchReconciledZeroQtyLotIds(): Promise<Set<string>> {
    const excludedLotIds = new Set<string>();
    try {
      const fetchXml = fetchQueries.sampleReonciliation.fetchReconciledZeroQtyLotsByUser;
      const rows = await this.dynamics.executeFetchQuery('indskr_samplereconciliations', fetchXml);
      if (!Array.isArray(rows)) return excludedLotIds;

      // Rows are ordered by lot asc, createdon desc, so the first row per lot is the latest submission.
      const seenLotIds = new Set<string>();
      rows.forEach((row: any) => {
        const rawLotId = row['_indskr_lot_value'] ?? row.indskr_lot;
        if (!rawLotId) return;
        const lotId = String(rawLotId).toLowerCase();
        if (seenLotIds.has(lotId)) return; // only consider the latest submission per lot
        seenLotIds.add(lotId);
        const physicalQty = row.indskr_physicalonhandqty;
        if (physicalQty === 0) {
          excludedLotIds.add(lotId);
        }
      });
    } catch (error) {
      console.error('Error fetching reconciled zero-qty lots:', error);
    }
    return excludedLotIds;
  }

  /**
   * OMNI-62549: returns the set of lot ids (lowercased) from the rep's LAST submitted physical count
   * that had a physical on-hand qty > 0 - the lots eligible for below-zero sampling on the drop screen
   * (AC-1/AC-2). Lots submitted as 0 are excluded (AC-3); lots not in that count are absent (AC-7/AC-9).
   *
   * Purely local (no server call): the set is persisted at submit time via
   * {@link saveLastPhysicalCountEligibleLots}, so this reads the same value online and offline.
   */
  async getLastPhysicalCountEligibleLotIds(): Promise<Set<string>> {
    return this.readCachedEligibleLotIds();
  }

  /**
   * OMNI-62549: persists the below-zero-eligible lot ids from a just-submitted reconciliation. Replaces
   * the previous set, since this submission is now the "last physical count". Only lots with a physical
   * on-hand qty > 0 are eligible (AC-3 excludes zero counts). Gated on RECIEPTS_TAB +
   * SAMPLE_RECONCILIATIONS feature actions and the BU_ALLOCATIONORDER_GA_ALLOWBELOWZERODROP ioConfig.
   */
  async saveLastPhysicalCountEligibleLots(items: Array<{ lotId?: string; physicalOnHandQty?: number }>): Promise<void> {
    if (!this.authservice.hasFeatureAction(FeatureActionsMap.RECIEPTS_TAB)
      || !this.authservice.hasFeatureAction(FeatureActionsMap.SAMPLE_RECONCILIATIONS)
      || !this.authservice.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_ALLOCATIONORDER_GA_ALLOWBELOWZERODROP)) return;
    const eligibleLotIds = new Set<string>();
    (items || []).forEach(item => {
      if (item?.lotId && typeof item.physicalOnHandQty === 'number' && item.physicalOnHandQty > 0) {
        eligibleLotIds.add(item.lotId);
      }
    });
    await this.cacheEligibleLotIds(eligibleLotIds);
  }

  /** OMNI-62549: reads the cached below-zero-eligible lot-id set from local DB. */
  private async readCachedEligibleLotIds(): Promise<Set<string>> {
    try {
      const doc = await this.disk.retrieve(DB_KEY_PREFIXES.SAMPLE_LAST_PHYSICAL_COUNT_ELIGIBLE_LOTS, true);
      if (doc && Array.isArray(doc.lotIds)) {
        return new Set<string>(doc.lotIds.map((id: string) => id));
      }
    } catch (error) {
      console.error('Error reading cached eligible lots:', error);
    }
    return new Set<string>();
  }

  /** OMNI-62549: persists the below-zero-eligible lot-id set to local DB (replaces the previous set). */
  private async cacheEligibleLotIds(ids: Set<string>): Promise<void> {
    try {
      await this.disk.updateOrInsert(DB_KEY_PREFIXES.SAMPLE_LAST_PHYSICAL_COUNT_ELIGIBLE_LOTS, () => ({
        _id: DB_KEY_PREFIXES.SAMPLE_LAST_PHYSICAL_COUNT_ELIGIBLE_LOTS,
        lotIds: Array.from(ids),
        lastUpdatedTime: new Date().getTime(),
      }));
    } catch (error) {
      console.error('Error caching eligible lots:', error);
    }
  }

  createSampleReconciliationRequestName() {
    const today = new Date();
    // name format - SR - 05 Feb 2026
    const formattedDate = this.dateTimeFormatsService.formatDate(today, 'DD MMM YYYY');
    return 'SR - ' +  formattedDate;
  }

  async createSampleReconciliationRequest(payload: any) {
    try {
      const url = this.authservice.userConfig.activeInstance.entryPointUrl + Endpoints.sample.CREATE_SAMPLE_RECONCILIATION_REQUEST;
      const response = await this.http.post(url, payload).toPromise();
      console.log('response', response);
      return response;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  /**
   * Discards the in-progress create form and clears selection. Call when user confirms discard.
   */
  discardCreateForm(): void {
    this.hasUnsavedCreateModeChanges = false;
    this.selectedReconciliationRequestId = '';
    this.selectedReconciliationRequest.next(null);
    this.selectedReconciliationLineItems = [];
  }

  /**
   * Shows discard confirmation alert. If user confirms, discards the create form and returns true.
   * Returns false if user cancels.
   * @param selectAfterDiscard When true, only clears the unsaved-changes flag and returns true.
   *   Use when discarding to select a list item—avoids emitting null which would trigger a race
   *   where create-mode init overwrites the selected item's data.
   */
  async confirmDiscardCreateForm(selectAfterDiscard?: boolean): Promise<boolean> {
    const res = await this.alertService.showAlert({
      title: this.translate.instant('DISCARD_CHANGES'),
      message: this.translate.instant('FIRST_TIME_EMAIL_DISCARD')
    }, this.translate.instant('DISCARD'));
    if (res?.role === 'ok') {
      if (selectAfterDiscard) {
        this.hasUnsavedCreateModeChanges = false;
      } else {
        this.discardCreateForm();
      }
      return true;
    }
    return false;
  }

  /**
   * Adds or updates a reconciliation request in the in-memory list and notifies subscribers.
   * Use after creating a new request so the list and selection stay in sync.
   */
  addReconciliationRequest(newRequest: SampleReconciliation): void {
    const requestId = newRequest.id;
    const existingIndex = this.sampleReconciliationRequests.findIndex(r => r.id === requestId);
    if (existingIndex >= 0) {
      this.sampleReconciliationRequests[existingIndex] = newRequest;
    } else {
      this.sampleReconciliationRequests.unshift(newRequest);
    }
    this.selectedReconciliationRequestId = requestId;
    this.selectedReconciliationRequest.next(newRequest);
    this.reconciliationRequestsUpdated.next(this.sampleReconciliationRequests);
  }
}